import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Slide,
  TextField,
  Divider,
  Box,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import * as taskTemplateStyle from "../fieldmap/tasktemplates/TaskTemplateStyle";
import { createSvgIcon } from '@mui/material/utils';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const initialHeight = 300;
const halfHeight = window.innerHeight * 0.5;
const maxHeight = window.innerHeight * 0.75;

const BottomSheetSubNestedDropdown = ({
  label,
  bottomSheetTitleText,
  bottomSheetTitleStyle,
  options,
  onChange,
  defaultValue = null,
  error = false,
  helperText = "",
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState("main");
  const [selectedItem, setSelectedItem] = useState(defaultValue || null);
  const [hasOpened, setHasOpened] = useState(false);
  const [isPristine, setIsPristine] = useState(true);
  const [dialogTitle, setDialogTitle] = useState(bottomSheetTitleText || label || "");
  const [height, setHeight] = useState(initialHeight);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(initialHeight);
  const dialogRef = useRef(null);
  const hasInitialized = useRef(false);

  // Keep track of the selected 'element' and 'subElement' for navigation purposes
  const [currentSelectedParent, setCurrentSelectedParent] = useState(null);
  const [currentSelectedSubParent, setCurrentSelectedSubParent] = useState(null);

  const ArrowIcon = createSvgIcon(
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
      <path d="M10.1836 17L15.1836 12L10.1836 7V17Z" fill="#3F4949" />
    </svg>,
    'ArrowIcon',
  );

  const TickMark = createSvgIcon(
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
      <path d="M9.18375 16.1698L5.01375 11.9998L3.59375 13.4098L9.18375 18.9998L21.1838 6.99984L19.7738 5.58984L9.18375 16.1698Z" fill="#3F4949" />
    </svg>,
    'TickMark',
  );

  useEffect(() => {
    setDialogTitle(bottomSheetTitleText || label || "");
  }, [bottomSheetTitleText, label]);

  // This useEffect ensures onChange is called only when the dialog closes AND a selection was made
  useEffect(() => {
    if (!open && onChange && selectedItem && !isPristine) {
      onChange(selectedItem);
      setIsPristine(true);
    }
  }, [open, onChange, selectedItem, isPristine]);

  // This useEffect makes sure the dialog title updates on open/close.
  useEffect(() => {
    setDialogTitle(bottomSheetTitleText || label || "");
  }, [open, bottomSheetTitleText, label]);

  const handleOpen = () => {
    setOpen(true);
    setCurrentStep("main");
    setHasOpened(true);
    setIsPristine(true);
    setHeight(initialHeight);
    setDialogTitle(bottomSheetTitleText || label || "");
    // Reset temporary navigation states
    setCurrentSelectedParent(selectedItem?.element || null);
    setCurrentSelectedSubParent(selectedItem?.subElement || null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFinalSelection = (item) => {
    setSelectedItem(item);
    setIsPristine(false);
    setOpen(false);
    // onChange is now handled by the useEffect on dialog close
  };

  const handleMainItemClick = (item) => {
    if (item.elements || item.leaves) {
      // If it has children, navigate to the next step
      setCurrentSelectedParent(item.element);
      setDialogTitle(item.element);
      setCurrentStep("sub");
    } else {
      // If it's a leaf node at the main level, select it directly
      handleFinalSelection({ element: item.element, subElement: null, leaf: null });
    }
  };

  const handleSubCategoryClick = (subElement) => {
    if (subElement.leaves) {
      // If it has leaves, navigate to the leaf step
      setCurrentSelectedSubParent(subElement.element);
      setDialogTitle(subElement.element);
      setCurrentStep("leaf");
    } else {
      // If it's a leaf node at the sub-category level, select it
      handleFinalSelection({
        element: currentSelectedParent,
        subElement: subElement.element,
        leaf: null,
      });
    }
  };

  const handleLeafSelect = (leaf) => {
    let finalItem;
    if (currentStep === "sub") {
      // Direct leaf under main category
      finalItem = {
        element: currentSelectedParent,
        subElement: null,
        leaf: leaf,
      };
    } else if (currentStep === "leaf") {
      // Leaf under a sub-category
      finalItem = {
        element: currentSelectedParent,
        subElement: currentSelectedSubParent,
        leaf: leaf,
      };
    }
    handleFinalSelection(finalItem);
  };

  const handleBack = () => {
    if (currentStep === "leaf") {
      setCurrentStep("sub");
      setDialogTitle(currentSelectedParent); // Title should be the main category's name
      setCurrentSelectedSubParent(null); // Clear sub-parent on back
    } else if (currentStep === "sub") {
      setCurrentStep("main");
      setDialogTitle(bottomSheetTitleText || label || "");
      setCurrentSelectedParent(null); // Clear parent on back
    } else {
      handleClose();
    }
  };

  const getSelectedText = () => {
    if (!selectedItem) return null;
    const { element, subElement, leaf } = selectedItem;
    // Only display subElement if it's not null, and leaf if it's not null.
    // If element is selected directly (no subElement/leaf), display only element.
    const parts = [element, subElement, leaf].filter(Boolean);
    return parts.join(" : ");
  };

  const displayErrorMessage = () => {
    return required && !open && hasOpened && !getSelectedText();
  };

  const isItemSelected = (item, currentLevel) => {
    if (!selectedItem) return false;

    if (currentLevel === "main") {
      // An item in the main list is "selected" only if it's the element
      // of the *fully selected* item AND it has no sub-elements/leaves,
      // meaning it was a direct main-level selection.
      return (
        selectedItem.element === item.element &&
        selectedItem.subElement === null &&
        selectedItem.leaf === null &&
        !item.elements &&
        !item.leaves
      );
    }

    if (currentLevel === "sub") {
      // Check if the current item is a subElement that was selected as final choice
      if (item.element && !item.leaves) { 
        return (
          selectedItem.element === currentSelectedParent &&
          selectedItem.subElement === item.element &&
          selectedItem.leaf === null
        );
      }
      // Check if the current item is a direct leaf under the main category
      else if (typeof item === 'string' || item.leaf) {
        const leafValue = typeof item === 'string' ? item : item.leaf;
        return (
          selectedItem.element === currentSelectedParent &&
          selectedItem.subElement === null &&
          selectedItem.leaf === leafValue
        );
      }
    }

    if (currentLevel === "leaf") {
      const leafValue = typeof item === 'string' ? item : item.leaf || item;
      return (
        selectedItem.element === currentSelectedParent &&
        selectedItem.subElement === currentSelectedSubParent &&
        selectedItem.leaf === leafValue
      );
    }

    return false;
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setStartY(e.touches[0].clientY);
    setCurrentHeight(height);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const deltaY = startY - e.touches[0].clientY;
    const newHeight = Math.max(currentHeight + deltaY, initialHeight);
    setHeight(newHeight);
  };

  const handleTouchEnd = () => {
    if (height < (halfHeight + initialHeight) / 2) {
      setHeight(initialHeight);
    } else if (height < (maxHeight + halfHeight) / 2) {
      setHeight(halfHeight);
    } else {
      setHeight(maxHeight);
    }
  };

  return (
    <>
      <TextField
        label={label}
        value={getSelectedText()}
        onClick={handleOpen}
        fullWidth
        error={displayErrorMessage()}
        helperText={displayErrorMessage() ? helperText || "Dette feltet er påkrevd" : " "}
        InputProps={{
          readOnly: true,
          style: { cursor: "pointer" },
          endAdornment: <ArrowDropDownIcon />,
        }}
        sx={{
          "& label": { color: "#3F4949", fontFamily: "Inter" },
          "& label.Mui-focused, & label.MuiFormLabel-filled": { color: "#00696D" },
        }}
          FormHelperTextProps={{
          sx: { height: '24px', margin: '0px' }
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.15)",
            backgroundColor: "#F2F4F4",
          },
        }}
        ref={dialogRef}
      >
        <Box
          onTouchStart={handleTouchStart}
          onTouchMove={handleDrag}
          onTouchEnd={handleTouchEnd}
          sx={{ textAlign: "center", padding: "15px 8px", cursor: "grab" }}
        >
          <Box
            sx={{
              width: 32,
              height: 4,
              backgroundColor: "#6F7979",
              borderRadius: 2,
              margin: "0 auto",
            }}
          />
        </Box>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "4px 8px",
            fontFamily: "Inter",
            fontWeight: 600,
            fontSize: "18px",
            color: "#3F4949",
            height: "64px",
            ...taskTemplateStyle.bottomSheetTitleStyle,
          }}
        >
          <IconButton onClick={handleBack} sx={{ gapLeft: "4px", padding: "8px" }}>
            {currentStep === "main" ? <CloseIcon /> : <ArrowBackIcon />}
          </IconButton>
          {dialogTitle}
        </DialogTitle>
        <Box sx={{ padding: "0px 16px" }}>
          <Divider />
        </Box>
        <DialogContent
          sx={{
            overflowY: "auto",
            padding: 0,
            maxHeight: '50vh', // or any suitable value like '60vh'
          }}
        >
          <List>
            {currentStep === "main" &&
              options.map((option) => (
                <ListItem
                  key={option.element}
                  button
                  onClick={() => handleMainItemClick(option)}
                  sx={{
                    height: 50, px: 2, "&:hover": { backgroundColor: "#f5f5f5" },
                    ...taskTemplateStyle.bottomSheetListItemStyle
                  }}
                >
                  <ListItemText primary={option.element} />
                  {(option.elements || option.leaves) ? (
                    <ListItemIcon sx={{ minWidth: "36px" }}>
                      <ArrowIcon fontSize="medium" alignItems="right" />
                    </ListItemIcon>
                  ) : (
                    isItemSelected(option, "main") && (
                      <ListItemIcon
                        sx={{
                          minWidth: '12px !important',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        <TickMark color="#00696D" fontSize="medium" />
                      </ListItemIcon>
                    )
                  )}
                </ListItem>
              ))}

            {currentStep === "sub" &&
              options
                .find((opt) => opt.element === currentSelectedParent)
                ?.elements?.map((subElement) => (
                  <ListItem 
                    key={subElement.element} 
                    button 
                    onClick={() => handleSubCategoryClick(subElement)} 
                    sx={{ 
                      height: 50, 
                      px: 2, 
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      ...taskTemplateStyle.bottomSheetListItemStyle 
                    }}
                  >
                    <ListItemText primary={subElement.element} />
                    {subElement.leaves ? (
                      <ListItemIcon sx={{ minWidth: "36px" }}>
                        <ArrowIcon fontSize="medium" alignItems="right" />
                      </ListItemIcon>
                    ) : (
                      // Show tick if this subElement is selected as final choice
                      isItemSelected(subElement, "sub") && (
                        <ListItemIcon
                          sx={{
                            minWidth: '12px !important',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <TickMark color="#00696D" fontSize="medium" />
                        </ListItemIcon>
                      )
                    )}
                  </ListItem>
                ))}
            {/* Direct leaves under the main category, displayed in "sub" step */}
            {currentStep === "sub" &&
              options
                .find((opt) => opt.element === currentSelectedParent)
                ?.leaves?.map((leaf) => (
                  <ListItem 
                    key={leaf} 
                    button 
                    onClick={() => handleLeafSelect(leaf)} 
                    sx={{ 
                      height: 50, 
                      px: 2, 
                      paddingRight: '12px',
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      ...taskTemplateStyle.bottomSheetListItemStyle 
                    }}
                  >
                    <ListItemText primary={leaf} />
                    {isItemSelected(leaf, "sub") && (
                      <ListItemIcon
                        sx={{
                          minWidth: '12px !important',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        <TickMark color="#00696D" fontSize="medium" />
                      </ListItemIcon>
                    )}
                  </ListItem>
                ))}

            {currentStep === "leaf" &&
              options
                .find((opt) => opt.element === currentSelectedParent)
                ?.elements?.find((sub) => sub.element === currentSelectedSubParent)
                ?.leaves?.map((leaf) => (
                  <ListItem 
                    key={leaf} 
                    button 
                    onClick={() => handleLeafSelect(leaf)} 
                    sx={{ 
                      height: 50, 
                      px: 2, 
                      paddingRight: '12px',
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      ...taskTemplateStyle.bottomSheetListItemStyle 
                    }}
                  >
                    <ListItemText primary={leaf} />
                    {isItemSelected(leaf, "leaf") && (
                      <ListItemIcon
                        sx={{
                          minWidth: '12px !important',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        <TickMark color="#00696D" fontSize="medium" />
                      </ListItemIcon>
                    )}
                  </ListItem>
                ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BottomSheetSubNestedDropdown;